import { defineComponent as _defineComponent } from 'vue'
import { IEventDiscount, IEventDiscountType, IEventTicketTier } from '@/enitites/event/model/types'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKCurrencyInput from '@/components/AppShared/BKCurrencyInput.vue'
import { ref, watch, computed } from 'vue'
import useValidationRules from '@/shared/composables/validation'
import BKCheckbox from '@/components/AppShared/BKCheckbox.vue'
import { useAddDiscount, useDeleteDiscount, useUpdateDiscount } from '@/modules/Payment/api/queries'
import { IDiscountPayload } from '@/modules/Payment/types'
import { BKConfirmationDialog, ModalWrapper } from '@/shared/ui/modals'
import Bugsnag from '@bugsnag/js'


export default /*#__PURE__*/_defineComponent({
  __name: 'EditTicketDiscountModal',
  props: {
    eventId: null,
    isOpen: { type: Boolean },
    discount: null,
    ticketTiers: null,
    eventStartTime: null,
    type: null
  },
  emits: ["close", "update:discount"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'update:discount'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  eventId: number
  isOpen: boolean
  discount: IEventDiscount | null
  ticketTiers: IEventTicketTier[]
  eventStartTime: Date | string | null
  type: IEventDiscountType | null
};





const title = ref('')
const discountType = ref<IEventDiscountType>(props.type || 'code')
const amount = ref(0)
const isPercentage = ref(false)
const applicableToAll = ref(true)
const selectedTicketIds = ref<number[]>([])
const ticketLimits = ref(-1)
const isLimitedTickets = ref(false)
const startNow = ref(true)
const startDate = ref('')
const startTime = ref('')
const endWhenSalesEnd = ref(true)
const endDate = ref('')
const endTime = ref('')
const code = ref('')
const isNew = computed(() => !props.discount?.id)
const isShowDeleteConfirmation = ref(false)

const isFormValid = ref(true)

const rules = useValidationRules()

const { mutate: addDiscount, isPending: isAddingDiscount } = useAddDiscount()
const { mutate: updateDiscount, isPending: isUpdatingDiscount } = useUpdateDiscount()
const { mutate: deleteDiscount, isPending: isDeletingDiscount } = useDeleteDiscount()

const isProcessing = computed(() => isAddingDiscount.value || isUpdatingDiscount.value || isDeletingDiscount.value)

function resetForm() {
  title.value = ''
  discountType.value = props.type || 'code'
  amount.value = 0
  isPercentage.value = false
  applicableToAll.value = true
  selectedTicketIds.value = []
  ticketLimits.value = -1
  isLimitedTickets.value = false
  startNow.value = true
  startDate.value = ''
  startTime.value = ''
  endWhenSalesEnd.value = true
  endDate.value = ''
  endTime.value = ''
  code.value = ''
}

// Convert eventStartTime to Date object if it's a string
const eventStartTimeAsDate = computed(() => {
  if (!props.eventStartTime) return null
  if (typeof props.eventStartTime === 'string') {
    return new Date(props.eventStartTime)
  }
  return props.eventStartTime
})

const valueOptions = [
  { text: '$', value: false },
  { text: '%', value: true },
]

const applicableOptions = computed(() => [
  { text: 'All tickets', value: true },
  { text: 'Certain ticket types', value: false, disabled: props.ticketTiers.length === 0 },
])

const startOptions = [
  { text: 'Now', value: true },
  { text: 'Scheduled time', value: false },
]

const endOptions = [
  { text: 'When ticket sales end', value: true },
  { text: 'Scheduled time', value: false },
]

const effectiveTicketLimits = computed(() => {
  return isLimitedTickets.value ? ticketLimits.value : -1
})

const validFrom = computed(() => {
  if (startNow.value) {
    return new Date()
  }
  if (startDate.value && startTime.value) {
    return new Date(`${startDate.value}T${startTime.value}`)
  }
  return new Date()
})

const validTo = computed(() => {
  if (endWhenSalesEnd.value) {
    return eventStartTimeAsDate.value || new Date()
  }
  if (endDate.value && endTime.value) {
    return new Date(`${endDate.value}T${endTime.value}`)
  }
  return new Date()
})

const applicableTicketTiers = computed(() => {
  return applicableToAll.value ? [] : selectedTicketIds.value
})

// Computed properties for displaying date/time values
const displayStartDate = computed(() => {
  if (startNow.value) {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return startDate.value
})

const displayStartTime = computed(() => {
  if (startNow.value) {
    const now = new Date()
    return now.toTimeString().slice(0, 5)
  }
  return startTime.value
})

const displayEndDate = computed(() => {
  if (endWhenSalesEnd.value && eventStartTimeAsDate.value) {
    return eventStartTimeAsDate.value.toISOString().split('T')[0]
  }
  return endDate.value
})

const displayEndTime = computed(() => {
  if (endWhenSalesEnd.value && eventStartTimeAsDate.value) {
    return eventStartTimeAsDate.value.toTimeString().slice(0, 5)
  }
  return endTime.value
})

// Computed properties for readonly states
const isStartInputsReadonly = computed(() => startNow.value)
const isEndInputsReadonly = computed(() => endWhenSalesEnd.value)

// Validation rules
const amountRules = computed(() => [
  rules.required,
  (value: string | number | undefined) => {
    if (!value) return 'Required value'
    const numericValue = typeof value === 'string' ? parseFloat(value) : value
    if (isPercentage.value) {
      return (numericValue >= 1 && numericValue <= 100) || 'Percentage must be between 1 and 100'
    }
    return numericValue >= 0 || 'Must be a non-negative number'
  },
])

const ticketLimitsRules = computed(() => [
  rules.required,
  (value: number | string | undefined) => {
    if (!value) return 'Required value'
    const numericValue = typeof value === 'string' ? parseInt(value, 10) : value
    return numericValue > 0 || 'Must be greater than 0'
  },
])

const startDateRules = computed(() => [
  (value: string | number | undefined) => {
    if (startNow.value) return true
    if (!value) return 'Required value'
    const selectedDate = new Date(`${value}T${startTime.value || '00:00'}`)
    const eventStart = eventStartTimeAsDate.value
    if (eventStart && selectedDate > eventStart) {
      return 'Start date cannot be after event start time'
    }
    if (endDate.value && endTime.value) {
      const endDateTime = new Date(`${endDate.value}T${endTime.value}`)
      if (selectedDate >= endDateTime) {
        return 'Start date must be before end date'
      }
    }
    return true
  },
])

const endDateRules = computed(() => [
  (value: string | number | undefined) => {
    if (endWhenSalesEnd.value) return true
    if (!value) return 'Required value'
    const selectedDate = new Date(`${value}T${endTime.value || '23:59'}`)
    if (startDate.value && startTime.value) {
      const startDateTime = new Date(`${startDate.value}T${startTime.value}`)
      if (selectedDate <= startDateTime) {
        return 'End date must be after start date'
      }
    }
    return true
  },
])

function handleAmountUpdate(value: string | number) {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      amount.value = 0
      return
    }
    const numericValue = parseFloat(value)
    if (Number.isNaN(numericValue)) {
      amount.value = 0
      return
    }
    if (isPercentage.value) {
      amount.value = Math.max(1, Math.min(100, numericValue))
    } else {
      amount.value = Math.max(0, numericValue)
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (isPercentage.value) {
      amount.value = Math.max(1, Math.min(100, value || 1))
    } else {
      amount.value = Math.max(0, value || 0)
    }
  }
}

function handleTicketLimitsUpdate(value: string | number) {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      ticketLimits.value = 1
      return
    }
    const numericValue = parseInt(value, 10)
    if (Number.isNaN(numericValue)) {
      ticketLimits.value = 1
      return
    }
    ticketLimits.value = Math.max(1, numericValue)
  } else {
    ticketLimits.value = Math.max(1, value || 1)
  }
}

function handleSave() {
  if (isProcessing.value) return

  const payload: IDiscountPayload = {
    title: discountType.value === 'code' ? code.value : 'Early Bird Pricing',
    description: '',
    amount: amount.value,
    isPercentage: isPercentage.value,
    applicableTicketTiers: applicableTicketTiers.value,
    ticketLimits: effectiveTicketLimits.value,
    validFrom: validFrom.value.toISOString(),
    validTo: validTo.value.toISOString(),
    type: discountType.value === 'code' ? 'code' : 'early_bird',
    ...(discountType.value === 'code' && { code: code.value }),
  }

  const callbacks = {
    onSuccess: () => {
      emit('update:discount')
      resetForm()
    },
    onError: (err: Error) => {
      Bugsnag.notify(err, (event) => {
        event.addMetadata('DiscountModal', {
          eventId: props.eventId,
          title: title.value,
          amount: amount.value,
          isPercentage: isPercentage.value,
          discountType: discountType.value,
        })
      })
    },
  }

  if (isNew.value) {
    addDiscount(
      {
        eventId: props.eventId,
        payload,
      },
      callbacks
    )
  } else if (props.discount?.id) {
    updateDiscount(
      {
        discountId: props.discount.id,
        payload,
      },
      callbacks
    )
  }
}

function handleDelete() {
  if (isProcessing.value || !props.discount?.id) return
  isShowDeleteConfirmation.value = false

  deleteDiscount(
    {
      discountId: props.discount.id,
      eventId: props.eventId,
    },
    {
      onSuccess: () => {
        emit('update:discount')
        resetForm()
      },
      onError: (err: Error) => {
        Bugsnag.notify(err, (event) => {
          event.addMetadata('DiscountModal_Delete', {
            eventId: props.eventId,
            discountId: props.discount?.id,
          })
        })
      },
    }
  )
}

function handleClose() {
  emit('close')
  resetForm()
}

// Watch for prop changes
watch(
  () => props.discount,
  (newDiscount) => {
    if (newDiscount) {
      title.value = newDiscount.title
      amount.value = newDiscount.amount
      isPercentage.value = newDiscount.isPercentage
      applicableToAll.value =
        newDiscount.applicableTicketTiers?.length === props.ticketTiers.length ||
        newDiscount.applicableTicketTiers?.length === 0
      selectedTicketIds.value = newDiscount.applicableTicketTiers || []
      ticketLimits.value = newDiscount.ticketLimits === -1 ? 1 : newDiscount.ticketLimits
      isLimitedTickets.value = newDiscount.ticketLimits !== -1
      discountType.value = props.type || newDiscount.type || 'code'
      code.value = newDiscount.code || ''

      const validFromDate = new Date(newDiscount.validFrom)
      const validToDate = new Date(newDiscount.validTo)

      // Handle dates
      const now = new Date()
      startNow.value = Math.abs(validFromDate.getTime() - now.getTime()) < 60000 // within 1 minute
      if (!startNow.value) {
        startDate.value = validFromDate.toISOString().split('T')[0]
        startTime.value = validFromDate.toTimeString().slice(0, 5)
      }

      const eventStart = eventStartTimeAsDate.value
      endWhenSalesEnd.value = eventStart ? Math.abs(validToDate.getTime() - eventStart.getTime()) < 60000 : true
      if (!endWhenSalesEnd.value) {
        endDate.value = validToDate.toISOString().split('T')[0]
        endTime.value = validToDate.toTimeString().slice(0, 5)
      }
    } else {
      resetForm()
    }
  },
  { immediate: true }
)

// Watch unlimited tickets toggle
watch(isLimitedTickets, (newValue) => {
  if (newValue) {
    ticketLimits.value = 1
  }
})

// Watch ticket tiers to auto-switch to "All tickets" when no tiers exist
watch(
  () => props.ticketTiers.length,
  (newLength) => {
    if (newLength === 0 && !applicableToAll.value) {
      applicableToAll.value = true
      selectedTicketIds.value = []
    }
  }
)

// Watch percentage type changes to adjust amount value
watch(isPercentage, (newIsPercentage) => {
  if (newIsPercentage && amount.value > 100) {
    amount.value = 100
  } else if (newIsPercentage && amount.value < 1) {
    amount.value = 1
  } else if (!newIsPercentage && amount.value < 0) {
    amount.value = 0
  }
})

watch(
  () => props.type,
  (newType) => {
    if (newType && !props.discount) {
      discountType.value = newType
    }
  },
  { immediate: true }
)

return { __sfc: true,props, emit, title, discountType, amount, isPercentage, applicableToAll, selectedTicketIds, ticketLimits, isLimitedTickets, startNow, startDate, startTime, endWhenSalesEnd, endDate, endTime, code, isNew, isShowDeleteConfirmation, isFormValid, rules, addDiscount, isAddingDiscount, updateDiscount, isUpdatingDiscount, deleteDiscount, isDeletingDiscount, isProcessing, resetForm, eventStartTimeAsDate, valueOptions, applicableOptions, startOptions, endOptions, effectiveTicketLimits, validFrom, validTo, applicableTicketTiers, displayStartDate, displayStartTime, displayEndDate, displayEndTime, isStartInputsReadonly, isEndInputsReadonly, amountRules, ticketLimitsRules, startDateRules, endDateRules, handleAmountUpdate, handleTicketLimitsUpdate, handleSave, handleDelete, handleClose, BKInput, BKCurrencyInput, BKCheckbox, BKConfirmationDialog, ModalWrapper }
}

})